import { useState, useCallback } from 'react'
import { get, post, put, del } from '../helper/network'
import API from '../helper/api'
import type {
  AdminDashboardStats,
  AdminConfig,
  AdminConfigCreateRequest,
  AdminConfigUpdateRequest,
  EnterpriseConfigOverride,
  EnterpriseConfigOverrideRequest,
  AdminPlan,
  AdminPlanCreateRequest,
  AdminPlanUpdateRequest,
  AdminOrderItem,
} from '../types'

export function useAdmin() {
  const [dashboard, setDashboard] = useState<AdminDashboardStats | null>(null)
  const [configs, setConfigs] = useState<AdminConfig[]>([])
  const [enterpriseOverrides, setEnterpriseOverrides] = useState<EnterpriseConfigOverride[]>([])
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [orders, setOrders] = useState<AdminOrderItem[]>([])
  const [orderTotal, setOrderTotal] = useState(0)
  const [orderPage, setOrderPage] = useState(0)
  const [orderTotalPages, setOrderTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)

  // ---- Dashboard ----

  const fetchDashboard = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_ADMIN_DASHBOARD)
      if (res?.code === 200 && res.data) {
        setDashboard(res.data)
      }
    } catch {}
    setLoading(false)
  }, [])

  // ---- Config ----

  const fetchConfigs = useCallback(async (group?: string) => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (group) params.group = group
      const res: any = await get(API.GET_ADMIN_CONFIG_LIST, params)
      if (res?.code === 200 && res.data) {
        setConfigs(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
    setLoading(false)
  }, [])

  const fetchConfigDetail = useCallback(async (id: string) => {
    try {
      const res: any = await get(`${API.GET_ADMIN_CONFIG_DETAIL}${id}`)
      if (res?.code === 200 && res.data) {
        return res.data as AdminConfig
      }
    } catch {}
    return null
  }, [])

  const createConfig = async (request: AdminConfigCreateRequest): Promise<boolean> => {
    try {
      const res: any = await post(API.POST_ADMIN_CONFIG_CREATE, request)
      return res?.code === 200
    } catch {}
    return false
  }

  const updateConfig = async (request: AdminConfigUpdateRequest): Promise<boolean> => {
    try {
      const res: any = await put(API.PUT_ADMIN_CONFIG_UPDATE, request)
      return res?.code === 200
    } catch {}
    return false
  }

  const deleteConfig = async (id: string): Promise<boolean> => {
    try {
      const res: any = await del(`${API.DELETE_ADMIN_CONFIG_DELETE}${id}`)
      return res?.code === 200
    } catch {}
    return false
  }

  const fetchEnterpriseOverrides = useCallback(async (enterpriseId: string) => {
    setLoading(true)
    try {
      const res: any = await get(`${API.GET_ADMIN_ENTERPRISE_CONFIG}${enterpriseId}`)
      if (res?.code === 200 && res.data) {
        setEnterpriseOverrides(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
    setLoading(false)
  }, [])

  const saveEnterpriseOverride = async (
    enterpriseId: string,
    request: EnterpriseConfigOverrideRequest
  ): Promise<boolean> => {
    try {
      const res: any = await post(`${API.POST_ADMIN_ENTERPRISE_CONFIG}${enterpriseId}`, request)
      return res?.code === 200
    } catch {}
    return false
  }

  // ---- Plan ----

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_ADMIN_PLAN_LIST)
      if (res?.code === 200 && res.data) {
        setPlans(Array.isArray(res.data) ? res.data : [])
      }
    } catch {}
    setLoading(false)
  }, [])

  const fetchPlanDetail = useCallback(async (id: string) => {
    try {
      const res: any = await get(`${API.GET_ADMIN_PLAN_DETAIL}${id}`)
      if (res?.code === 200 && res.data) {
        return res.data as AdminPlan
      }
    } catch {}
    return null
  }, [])

  const createPlan = async (request: AdminPlanCreateRequest): Promise<boolean> => {
    try {
      const res: any = await post(API.POST_ADMIN_PLAN_CREATE, request)
      return res?.code === 200
    } catch {}
    return false
  }

  const updatePlan = async (request: AdminPlanUpdateRequest): Promise<boolean> => {
    try {
      const res: any = await put(API.PUT_ADMIN_PLAN_UPDATE, request)
      return res?.code === 200
    } catch {}
    return false
  }

  const deletePlan = async (id: string): Promise<boolean> => {
    try {
      const res: any = await del(`${API.DELETE_ADMIN_PLAN_DELETE}${id}`)
      return res?.code === 200
    } catch {}
    return false
  }

  const togglePlan = async (id: string): Promise<boolean> => {
    try {
      const res: any = await put(`${API.PUT_ADMIN_PLAN_TOGGLE}${id}`)
      return res?.code === 200
    } catch {}
    return false
  }

  // ---- Order ----

  const fetchOrders = useCallback(async (pageNum = 1, pageSize = 10) => {
    setLoading(true)
    try {
      const res: any = await get(API.GET_ADMIN_ORDER_LIST, { pageNum, pageSize })
      if (res?.code === 200 && res.data) {
        const d = res.data
        if (d.records) {
          setOrders(d.records)
          setOrderTotal(d.total || 0)
          setOrderPage((d.current || 1) - 1)
          setOrderTotalPages(d.pages || 1)
        } else if (d.content) {
          setOrders(d.content)
          setOrderTotal(d.totalElements || 0)
          setOrderPage(d.number || 0)
          setOrderTotalPages(d.totalPages || 1)
        } else if (Array.isArray(d)) {
          setOrders(d)
          setOrderTotal(d.length)
          setOrderPage(0)
          setOrderTotalPages(1)
        }
      }
    } catch {}
    setLoading(false)
  }, [])

  const fetchOrderDetail = useCallback(async (orderNo: string) => {
    try {
      const res: any = await get(`${API.GET_ADMIN_ORDER_DETAIL}${orderNo}`)
      if (res?.code === 200 && res.data) {
        return res.data as AdminOrderItem
      }
    } catch {}
    return null
  }, [])

  return {
    dashboard,
    configs,
    enterpriseOverrides,
    plans,
    orders,
    orderTotal,
    orderPage,
    orderTotalPages,
    loading,
    fetchDashboard,
    fetchConfigs,
    fetchConfigDetail,
    createConfig,
    updateConfig,
    deleteConfig,
    fetchEnterpriseOverrides,
    saveEnterpriseOverride,
    fetchPlans,
    fetchPlanDetail,
    createPlan,
    updatePlan,
    deletePlan,
    togglePlan,
    fetchOrders,
    fetchOrderDetail,
  }
}
